// BiometricVault - web replacement for the native biometric keystore.
//
// Secrets are AES-GCM encrypted and kept in IndexedDB. The wrapping key comes
// from one of two modes, chosen at enrollment based on what the browser offers:
//
//   "prf" - key derived (HKDF) from the WebAuthn PRF extension output of a
//           platform passkey. Decryption is impossible without a fresh
//           biometric/PIN assertion, so data at rest is bound to the
//           authenticator (Face ID / Touch ID / Windows Hello / Android).
//
//   "key" - non-extractable AES-GCM CryptoKey stored in IndexedDB. Decryption
//           is silent; when a platform passkey exists it is still used as a
//           user-verification gate before interactive unlocks.
//
// A short-lived in-memory PRF cache lets an enrollment assertion be reused for
// an immediately following encrypt, so first-run setup only prompts once.

const DB_NAME = "tide-auth-vault";
const DB_VERSION = 1;
const STORE = "vault";

const CRED_RECORD = "webauthn-credential";
const PRF_REUSE_MS = 60 * 1000;

const te = new TextEncoder();
const td = new TextDecoder();

/* ---------------- IndexedDB helpers ---------------- */

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(key) {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const req = db.transaction(STORE, "readonly").objectStore(STORE).get(key);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
}

async function idbSet(key, value) {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(value, key);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

async function idbDelete(key) {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(key);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

/* ---------------- WebAuthn helpers ---------------- */

function randomBytes(n) {
  const b = new Uint8Array(n);
  crypto.getRandomValues(b);
  return b;
}

async function platformAuthenticatorAvailable() {
  try {
    if (!window.PublicKeyCredential) return false;
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

// In-memory reuse window so enroll + encrypt = one prompt.
let _prfCache = { output: null, at: 0 };

function _cachePrf(output) {
  _prfCache = { output, at: Date.now() };
}
function _takeCachedPrf() {
  if (_prfCache.output && Date.now() - _prfCache.at < PRF_REUSE_MS) {
    return _prfCache.output;
  }
  _prfCache = { output: null, at: 0 };
  return null;
}

async function loadCredRecord() {
  return idbGet(CRED_RECORD);
}

// The PRF salt is FIXED, not random.
//
// A random per-enrolment salt has to be stored, and anything stored in this
// origin dies when site data is cleared - so the passkey would survive the wipe
// (it lives in the platform keychain, and syncs) while the salt needed to
// reproduce its PRF output would not. The vault would be undecryptable with the
// authenticator sitting right there.
//
// A PRF salt is not a secret. It is domain separation: the secret is the
// per-credential key inside the authenticator, which never leaves it. Deriving
// the salt from a constant label costs nothing and makes the wrapping key a
// pure function of (passkey, label) - reproducible on any device the passkey
// syncs to, after any wipe.
const PRF_SALT_LABEL = "tide-authenticator/vault/prf/v1";
// Domain separation: the backup key and any credential key are different keys
// off the same PRF output.
const BACKUP_INFO_LABEL = "tide-vault/backup/v1";
let _fixedSaltPromise = null;

async function fixedPrfSalt() {
  if (!_fixedSaltPromise) {
    _fixedSaltPromise = crypto.subtle
      .digest("SHA-256", te.encode(PRF_SALT_LABEL))
      .then((b) => new Uint8Array(b));
  }
  return _fixedSaltPromise;
}

/** The salt for a record: legacy records keep the random one they enrolled with. */
async function saltFor(cred) {
  if (cred && cred.prfSaltFixed !== true && cred.prfSalt) return cred.prfSalt;
  return fixedPrfSalt();
}

// Create a platform passkey; returns the stored credential record or null when
// WebAuthn enrollment is not possible.
async function enrollCredential() {
  if (!(await platformAuthenticatorAvailable())) return null;

  const prfSalt = await fixedPrfSalt();
  try {
    const credential = await navigator.credentials.create({
      publicKey: {
        challenge: randomBytes(32),
        rp: { name: "Tide Authenticator", id: location.hostname },
        user: {
          id: randomBytes(16),
          name: "device",
          displayName: "This device",
        },
        pubKeyCredParams: [
          { alg: -7, type: "public-key" },   // ES256
          { alg: -257, type: "public-key" }, // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: "platform",
          // Discoverable, so the platform can offer this passkey when we have
          // no credential id to ask for - which is exactly the state after site
          // data is cleared.
          residentKey: "required",
          userVerification: "required",
        },
        timeout: 60000,
        extensions: { prf: { eval: { first: prfSalt } } },
      },
    });

    const ext = credential.getClientExtensionResults?.() || {};
    const prfEnabled = !!(ext.prf?.enabled || ext.prf?.results?.first);
    if (ext.prf?.results?.first) {
      _cachePrf(new Uint8Array(ext.prf.results.first));
    }

    const record = {
      credId: new Uint8Array(credential.rawId),
      prfEnabled,
      prfSalt,
      // Marks this record as using the fixed salt. Absent on records enrolled
      // before that change, which must keep using their stored random salt.
      prfSaltFixed: true,
    };
    await idbSet(CRED_RECORD, record);
    return record;
  } catch (e) {
    console.warn("Vault: passkey enrollment failed, using key mode:", e?.message || e);
    return null;
  }
}

// Run a user-verifying assertion. Returns the PRF output (Uint8Array) when the
// credential supports PRF, otherwise null. Throws if the user cancels.
async function assertIdentity(cred, { wantPrf = true } = {}) {
  const publicKey = {
    challenge: randomBytes(32),
    rpId: location.hostname,
    // An empty allowlist asks the platform to offer its discoverable passkeys
    // for this RP. That is the only way in after a wipe, when the credential id
    // we would normally name is gone along with everything else.
    allowCredentials: cred?.credId
      ? [{ id: cred.credId, type: "public-key" }]
      : [],
    userVerification: "required",
    timeout: 60000,
  };
  if (wantPrf && (cred?.prfEnabled ?? true)) {
    publicKey.extensions = { prf: { eval: { first: await saltFor(cred) } } };
  }

  const assertion = await navigator.credentials.get({ publicKey });
  const ext = assertion.getClientExtensionResults?.() || {};
  if (ext.prf?.results?.first) {
    const out = new Uint8Array(ext.prf.results.first);
    _cachePrf(out);
    return out;
  }
  return null;
}

/* ---------------- Crypto helpers ---------------- */

async function deriveAesKeyFromPrf(prfOutput, infoLabel) {
  const hkdfKey = await crypto.subtle.importKey("raw", prfOutput, "HKDF", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt: new Uint8Array(32), info: te.encode(infoLabel) },
    hkdfKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

async function aesEncrypt(key, plaintext) {
  const iv = randomBytes(12);
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, te.encode(plaintext));
  return { iv, ct: new Uint8Array(ct) };
}

async function aesDecrypt(key, iv, ct) {
  const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ct);
  return td.decode(pt);
}

/* ---------------- Public API ---------------- */

const AUTH_REQUIRED = "AUTH_REQUIRED";

const BiometricVault = {
  AUTH_REQUIRED,

  async isSupported() {
    return platformAuthenticatorAvailable();
  },

  // Silent presence check - never prompts.
  async hasCredentials({ server }) {
    try {
      const rec = await idbGet(`secret:${server}`);
      return !!rec;
    } catch {
      return false;
    }
  },

  // User-verification ceremony. Enrolls the platform passkey on first use.
  // Resolves true on success; throws if the user cancels. When no platform
  // authenticator exists this is a no-op (vault falls back to "key" mode).
  async verifyIdentity() {
    let cred = await loadCredRecord();
    if (!cred) {
      cred = await enrollCredential();
      if (cred) return true; // create() already user-verified
      return true;           // no authenticator available - nothing to gate with
    }
    await assertIdentity(cred);
    return true;
  },

  async setCredentials({ username = "device", password, server }) {
    if (!password) throw new Error("No secret provided");

    let cred = await loadCredRecord();
    if (!cred) cred = await enrollCredential();

    let record;
    if (cred?.prfEnabled) {
      let prf = _takeCachedPrf();
      if (!prf) prf = await assertIdentity(cred);
      if (!prf) throw new Error("PRF output unavailable");
      const key = await deriveAesKeyFromPrf(prf, `tide-vault/${server}`);
      const { iv, ct } = await aesEncrypt(key, password);
      record = { mode: "prf", username, iv, ct };
    } else {
      const wrapKey = await crypto.subtle.generateKey(
        { name: "AES-GCM", length: 256 },
        false, // non-extractable; stored as a structured-clonable CryptoKey
        ["encrypt", "decrypt"]
      );
      const { iv, ct } = await aesEncrypt(wrapKey, password);
      record = { mode: "key", username, iv, ct, wrapKey };
    }

    await idbSet(`secret:${server}`, record);
    return true;
  },

  // interactive=false: only succeed if no prompt is needed ("key" mode, or a
  // PRF output cached moments ago). Throws Error(AUTH_REQUIRED) otherwise.
  async getCredentials({ server, interactive = true }) {
    const record = await idbGet(`secret:${server}`);
    if (!record) throw new Error("No credentials found");

    if (record.mode === "prf") {
      const cred = await loadCredRecord();
      if (!cred) throw new Error("Vault credential missing");
      let prf = _takeCachedPrf();
      if (!prf) {
        if (!interactive) throw new Error(AUTH_REQUIRED);
        prf = await assertIdentity(cred);
      }
      if (!prf) throw new Error("PRF output unavailable");
      const key = await deriveAesKeyFromPrf(prf, `tide-vault/${server}`);
      const password = await aesDecrypt(key, record.iv, record.ct);
      return { username: record.username, password };
    }

    // "key" mode - silent decrypt; gate interactive unlocks when a passkey exists.
    if (interactive) {
      const cred = await loadCredRecord();
      if (cred) await assertIdentity(cred, { wantPrf: false });
    }
    const password = await aesDecrypt(record.wrapKey, record.iv, record.ct);
    return { username: record.username, password };
  },

  async deleteCredentials({ server }) {
    await idbDelete(`secret:${server}`);
    return true;
  },

  // Full wipe including the passkey record (the passkey itself stays on the
  // authenticator but becomes orphaned and unused).
  /**
   * Encrypt an arbitrary blob under the passkey's PRF key.
   *
   * Exists so the on-disk vault file is protected by the SAME authenticator the
   * device is already bound to - no second secret, nothing for the user to
   * remember, and no key ever leaves the authenticator. The HKDF info label is
   * distinct from the per-server credential label, so a backup key and a
   * credential key are different keys derived from the same PRF output.
   *
   * PRF mode only. In "key" mode the wrapping key is a non-extractable
   * CryptoKey by construction, so its data cannot be carried off the device at
   * all - callers are told rather than handed something that will not restore.
   *
   * @param {string} plaintext
   * @returns {Promise<{mode:"prf", iv:number[]|Uint8Array, ct:number[]|Uint8Array}>}
   */
  async encryptBlob(plaintext) {
    const cred = await loadCredRecord();
    if (!cred?.prfEnabled) {
      throw new Error("This device wraps its vault with a non-exportable key, so it cannot be backed up to a file");
    }
    let prf = _takeCachedPrf();
    if (!prf) prf = await assertIdentity(cred);
    if (!prf) throw new Error("PRF output unavailable");

    const key = await deriveAesKeyFromPrf(prf, BACKUP_INFO_LABEL);
    const { iv, ct } = await aesEncrypt(key, plaintext);
    return { mode: "prf", iv, ct };
  },

  /**
   * Decrypt a blob produced by encryptBlob, on this or any device the passkey
   * reaches. Deliberately does NOT need a stored credential record: after a
   * wipe there is none, and the assertion falls back to a discoverable passkey.
   */
  async decryptBlob(record) {
    if (!record || record.mode !== "prf") throw new Error("Unsupported vault file");

    let prf = _takeCachedPrf();
    // `null` cred is the post-wipe case - assertIdentity then asks the platform
    // for any discoverable passkey for this origin and uses the fixed salt.
    if (!prf) prf = await assertIdentity(await loadCredRecord());
    if (!prf) throw new Error("PRF output unavailable");

    const key = await deriveAesKeyFromPrf(prf, BACKUP_INFO_LABEL);
    return aesDecrypt(key, record.iv, record.ct);
  },

  async wipe() {
    const db = await openDb();
    try {
      await new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, "readwrite");
        tx.objectStore(STORE).clear();
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => reject(tx.error);
      });
    } finally {
      db.close();
    }
    _prfCache = { output: null, at: 0 };
  },
};

export { BiometricVault };
